// Horizontal filter bar for the admin startups list.
// On mobile: collapsed behind a toggle button. On desktop: always visible in rows.
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { StartupFilters } from '../types/startup';
import { useFieldOptions } from '../hooks/useFieldOptions';

interface Props {
  filters: StartupFilters;
  onChange: (updated: StartupFilters) => void;
}

// Label + control stacked vertically — keeps all controls bottom-aligned via items-end on parent
// Caption + control. Rendered as a <label> so the wrapped select/input is named by its
// caption; `asLabel={false}` for controls that open their own popover of buttons (a label
// would forward clicks inside the popover to the toggle).
const FilterItem: React.FC<{ label: string; children: React.ReactNode; asLabel?: boolean }> = ({
  label,
  children,
  asLabel = true,
}) => {
  const Wrapper = asLabel ? 'label' : 'div';
  return (
    <Wrapper className="flex flex-col gap-1.5 shrink-0">
      <span className="text-xs font-medium text-base-content/50 whitespace-nowrap">{label}</span>
      {children}
    </Wrapper>
  );
};

// On desktop: fixed width so all rows align. On mobile: full width to act as a section header.
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-xs uppercase tracking-widest font-bold text-base-content/25 whitespace-nowrap self-end pb-2.5 shrink-0 w-full md:w-24">
    {children}
  </span>
);

// Consistent select sizing used across all filter controls
const selectCls = (active: boolean) =>
  `select select-bordered select-sm w-36 ${active ? 'select-primary' : ''}`;

const StartupsFilterPanel: React.FC<Props> = ({ filters, onChange }) => {
  const { t } = useTranslation('admin');
  const [industryOpen, setIndustryOpen] = useState(false);
  const industryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (industryRef.current && !industryRef.current.contains(e.target as Node)) {
        setIndustryOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const set = (key: keyof StartupFilters) => (value: string) =>
    onChange({ ...filters, [key]: value || undefined });

  const toggleIndustry = (val: string) => {
    const current = filters.industry ?? [];
    const next = current.includes(val) ? current.filter((i) => i !== val) : [...current, val];
    onChange({ ...filters, industry: next.length ? next : undefined });
  };

  const { options: industries } = useFieldOptions('industry');
  const { options: businessModels } = useFieldOptions('business_model_type');
  const { options: profitableOptions } = useFieldOptions('is_profitable');

  const industryCount = filters.industry?.length ?? 0;

  const industryButton = (
    <div ref={industryRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setIndustryOpen((o) => !o)}
        aria-label={
          industryCount > 0
            ? `${t('startups.filters.labels.industry')} (${industryCount})`
            : `${t('startups.filters.labels.industry')}: ${t('startups.filters.any')}`
        }
        aria-haspopup="true"
        aria-expanded={industryOpen}
        className={`select select-bordered select-sm w-36 flex items-center justify-between text-left ${
          industryCount > 0 ? 'select-primary' : ''
        }`}
      >
        <span className="flex-1 truncate text-sm leading-none">
          {industryCount > 0
            ? `${t('startups.filters.labels.industry')} (${industryCount})`
            : t('startups.filters.any')}
        </span>
      </button>
      {industryOpen && (
        <div className="absolute top-full left-0 mt-1 z-30 bg-base-100 border border-base-200 rounded-xl shadow-xl p-3 w-64">
          <p className="text-xs font-semibold text-base-content/50 mb-2 uppercase tracking-wider">
            {t('startups.filters.labels.industry')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {industries.map((ind) => {
              const active = filters.industry?.includes(ind.value);
              return (
                <button
                  key={ind.value}
                  type="button"
                  onClick={() => toggleIndustry(ind.value)}
                  className={`badge cursor-pointer border text-xs transition-colors ${
                    active ? 'badge-primary' : 'badge-ghost hover:badge-outline'
                  }`}
                >
                  {ind.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* ── Row 1: Basic ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3 md:gap-4">
        <SectionLabel>{t('startups.filters.sections.basic')}</SectionLabel>

        <FilterItem label={t('startups.filters.labels.stage')}>
          <select
            className={selectCls(!!filters.stage)}
            value={filters.stage ?? ''}
            onChange={(e) => set('stage')(e.target.value)}
          >
            <option value="">{t('startups.filters.any')}</option>
            <option value="idea">{t('startups.filters.options.stage.idea')}</option>
            <option value="mvp">{t('startups.filters.options.stage.mvp')}</option>
            <option value="early">{t('startups.filters.options.stage.early')}</option>
            <option value="growth">{t('startups.filters.options.stage.growth')}</option>
            <option value="scale">{t('startups.filters.options.stage.scale')}</option>
          </select>
        </FilterItem>

        <FilterItem label={t('startups.filters.labels.industry')} asLabel={false}>
          {industryButton}
        </FilterItem>

        <FilterItem label={t('startups.filters.labels.businessModel')}>
          <select
            className={selectCls(!!filters.businessModel)}
            value={filters.businessModel ?? ''}
            onChange={(e) => set('businessModel')(e.target.value)}
          >
            <option value="">{t('startups.filters.any')}</option>
            {businessModels.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FilterItem>

        <FilterItem label={t('startups.filters.labels.country')}>
          <input
            type="text"
            className={`input input-bordered input-sm w-32 ${filters.country ? 'input-primary' : ''}`}
            placeholder={t('startups.filters.placeholders.country')}
            value={filters.country ?? ''}
            onChange={(e) => onChange({ ...filters, country: e.target.value || undefined })}
          />
        </FilterItem>
      </div>

      {/* ── Row 2: Traction ──────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3 md:gap-4">
        <SectionLabel>{t('startups.filters.sections.traction')}</SectionLabel>

        <FilterItem label={t('startups.filters.labels.revenueRange')}>
          <select
            className={selectCls(!!filters.revenueRange)}
            value={filters.revenueRange ?? ''}
            onChange={(e) => set('revenueRange')(e.target.value)}
          >
            <option value="">{t('startups.filters.any')}</option>
            <option value="none">{t('startups.filters.options.revenueRange.none')}</option>
            <option value="1-5k">{t('startups.filters.options.revenueRange.k1_5')}</option>
            <option value="5k-20k">{t('startups.filters.options.revenueRange.k5_20')}</option>
            <option value="20k-100k">{t('startups.filters.options.revenueRange.k20_100')}</option>
            <option value="100k+">{t('startups.filters.options.revenueRange.k100plus')}</option>
          </select>
        </FilterItem>

        <FilterItem label={t('startups.filters.labels.growthRate')}>
          <select
            className={selectCls(!!filters.growthRate)}
            value={filters.growthRate ?? ''}
            onChange={(e) => set('growthRate')(e.target.value)}
          >
            <option value="">{t('startups.filters.any')}</option>
            <option value="lt5">{t('startups.filters.options.growthRate.lt5')}</option>
            <option value="5-15">{t('startups.filters.options.growthRate.p5_15')}</option>
            <option value="15-30">{t('startups.filters.options.growthRate.p15_30')}</option>
            <option value="gt30">{t('startups.filters.options.growthRate.gt30')}</option>
          </select>
        </FilterItem>

        <FilterItem label={t('startups.filters.labels.profitable')}>
          <select
            className={selectCls(!!filters.profitable)}
            value={filters.profitable ?? ''}
            onChange={(e) => set('profitable')(e.target.value)}
          >
            <option value="">{t('startups.filters.any')}</option>
            {profitableOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FilterItem>
      </div>

      {/* ── Row 3: Capital ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3 md:gap-4">
        <SectionLabel>{t('startups.filters.sections.capital')}</SectionLabel>

        <FilterItem label={t('startups.filters.labels.raisingCapital')}>
          <select
            className={selectCls(!!filters.raisingCapital)}
            value={filters.raisingCapital ?? ''}
            onChange={(e) => set('raisingCapital')(e.target.value)}
          >
            <option value="">{t('startups.filters.any')}</option>
            <option value="no">{t('startups.filters.options.raisingCapital.no')}</option>
            <option value="yes">{t('startups.filters.options.raisingCapital.yes')}</option>
            <option value="soon">{t('startups.filters.options.raisingCapital.soon')}</option>
          </select>
        </FilterItem>

        <FilterItem label={t('startups.filters.labels.priorInvestment')}>
          <select
            className={selectCls(!!filters.priorInvestment)}
            value={filters.priorInvestment ?? ''}
            onChange={(e) => set('priorInvestment')(e.target.value)}
          >
            <option value="">{t('startups.filters.any')}</option>
            <option value="bootstrapped">
              {t('startups.filters.options.priorInvestment.bootstrapped')}
            </option>
            <option value="angel">{t('startups.filters.options.priorInvestment.angel')}</option>
            <option value="vc">{t('startups.filters.options.priorInvestment.vc')}</option>
          </select>
        </FilterItem>
      </div>

      {/* ── Row 4: Team ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3 md:gap-4">
        <SectionLabel>{t('startups.filters.sections.team')}</SectionLabel>

        <FilterItem label={t('startups.filters.labels.fullTimeStatus')}>
          <select
            className={selectCls(!!filters.fullTimeStatus)}
            value={filters.fullTimeStatus ?? ''}
            onChange={(e) => set('fullTimeStatus')(e.target.value)}
          >
            <option value="">{t('startups.filters.any')}</option>
            <option value="all">{t('startups.filters.options.fullTimeStatus.all')}</option>
            <option value="some">{t('startups.filters.options.fullTimeStatus.some')}</option>
            <option value="parttime">
              {t('startups.filters.options.fullTimeStatus.parttime')}
            </option>
          </select>
        </FilterItem>

        <FilterItem label={t('startups.filters.labels.founderCount')}>
          <select
            className={selectCls(!!filters.founderCount)}
            value={filters.founderCount ?? ''}
            onChange={(e) => set('founderCount')(e.target.value)}
          >
            <option value="">{t('startups.filters.any')}</option>
            <option value="1">{t('startups.filters.options.founderCount.one')}</option>
            <option value="2">{t('startups.filters.options.founderCount.two')}</option>
            <option value="3+">{t('startups.filters.options.founderCount.threePlus')}</option>
          </select>
        </FilterItem>
      </div>
    </div>
  );
};

export default StartupsFilterPanel;
